import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, CheckCircle, Clock, AlertCircle, ArrowRight,
  Loader2, RefreshCw, CreditCard
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyApplicationsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`${API}/my-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(res.data);
    } catch (e) {
      console.error("Error:", e);
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-600', icon: Clock, text: 'Submitted' },
      under_review: { bg: 'bg-yellow-600', icon: Clock, text: 'Under Review' },
      evaluated: { bg: 'bg-purple-600', icon: CheckCircle, text: 'Evaluated' },
      accepted: { bg: 'bg-green-600', icon: CheckCircle, text: 'Accepted' },
      rejected: { bg: 'bg-red-600', icon: AlertCircle, text: 'Not Selected' },
      paid: { bg: 'bg-green-600', icon: CreditCard, text: 'Enrolled' }
    };
    const style = styles[status] || styles.submitted;
    return (
      <Badge className={`${style.bg} flex items-center gap-1`}>
        <style.icon className="h-3 w-3" />{style.text}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <Header />
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Applications</h1>
          <Button onClick={fetchApplications} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : applications.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Yet</h3>
              <p className="text-gray-500 mb-6">Start your journey by applying to SKI AXIS Accelerator</p>
              <Button onClick={() => navigate('/apply')} className="bg-green-600 hover:bg-green-700">
                Apply Now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.id} className="hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{app.company_name || 'Application'}</h3>
                      <p className="text-sm text-gray-500">ID: {app.id?.slice(0, 8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">Submitted: {new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(app.status)}
                      {app.status === 'accepted' && (
                        <Button onClick={() => navigate('/pricing')} className="bg-green-600">
                          <CreditCard className="h-4 w-4 mr-2" />Pay & Enroll
                        </Button>
                      )}
                    </div>
                  </div>
                  {app.evaluation && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">Nova AI Score: {app.evaluation.overall_score}/100</p>
                      <p className="text-sm text-gray-500">{app.evaluation.summary}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplicationsPage;
